import { type FC } from 'react';

import { Text, VStack } from '@chakra-ui/react';
import { type OptionBase } from 'chakra-react-select';
import { useTranslation } from 'react-i18next';

import { Select } from '@/components';
import { InterviewStatus } from '@/services/api';

import { statusLabel } from '../interview/statusLabel';
import {
  type ChangeCandidateEvaluationParams,
  useChangeCandidateEvaluationMutation,
} from './useAdminCandidateQueries';

type CandidateSuitabilityActionsProps = {
  candidateId: string;
  interviewId: string;
  status: InterviewStatus;
};

interface EvaluationOptions extends OptionBase {
  label: string;
  value: EvaluationInfo;
}

type EvaluationInfo = (typeof evaluationItems)[number];

const toOptions = (evaluationItem: EvaluationInfo): EvaluationOptions => {
  return {
    label: statusLabel(evaluationItem),
    value: evaluationItem,
  };
};

export const evaluationItems = [
  InterviewStatus.Pending,
  InterviewStatus.Positive,
  InterviewStatus.Negative,
  InterviewStatus.Hired,
  InterviewStatus.PreliminaryCompleted,
  InterviewStatus.OfferSent,
  InterviewStatus.OfferRejected,
  InterviewStatus.OfferAccepted,
  InterviewStatus.ReferenceCheck,
  InterviewStatus.Withdrawn,
] as const;

export const CandidateSuitabilityActions: FC<
  CandidateSuitabilityActionsProps
> = ({ candidateId, interviewId, status: currStatus }) => {
  const { t } = useTranslation(['interview', 'common']);

  const changeCandidateEvaluationMutation =
    useChangeCandidateEvaluationMutation();

  const handleChangeEvaluation = async (evaluation: EvaluationInfo) => {
    if (evaluation === currStatus) return;

    const data: ChangeCandidateEvaluationParams = {
      candidateId,
      interviewsId: interviewId,
      body: {
        status: evaluation,
      },
    };

    await changeCandidateEvaluationMutation.mutateAsync(data);
  };

  const canNotBeMarked: boolean =
    currStatus === InterviewStatus.Added ||
    currStatus === InterviewStatus.Invited;

  return (
    <>
      <VStack w="full" gap={1}>
        <Text w="full" color="gray.700" fontWeight="medium" fontSize="sm">
          {t('interview:videoInterview.isCandidateSuitable')}
        </Text>

        <Select
          aria-label={t('interview:videoInterview.isCandidateSuitable')}
          placeholder={t('interview:videoInterview.isCandidateSuitable')}
          value={evaluationItems
            .map(toOptions)
            .find((item) => item.value === currStatus)}
          onChange={(evaluationItem) => {
            if (evaluationItem) handleChangeEvaluation(evaluationItem.value);
          }}
          options={evaluationItems.map(toOptions)}
          size="sm"
          isLoading={changeCandidateEvaluationMutation.isPending}
          styles={{ container: (provided) => ({ ...provided, width: '100%' }) }}
          isDisabled={canNotBeMarked}
        />
      </VStack>
    </>
  );
};
